/**
 * Hooks LeadBeton Sprint 4 (Modules 9 Chantiers/Réclamations · 10 Facture auto + Balance âgée).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  operationsApi,
  type CreateReclamationPayload,
  type CreerAvoirPayload,
  type GenererFacturePayload,
  type ReclamationListParams,
  type UpdateReclamationPayload,
} from '@/api/operations';
import { financeApi } from '@/api/finance';

const RECLAMATIONS_KEY  = ['operations', 'beton', 'reclamations'] as const;
const BALANCE_AGEE_KEY  = ['finance', 'balance-agee']             as const;

// ── Facture auto depuis BL ───────────────────────────────────────────────

export const useGenererFactureBL = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ expeditionId, payload = {} }: { expeditionId: string; payload?: GenererFacturePayload }) =>
      operationsApi.beton.genererFactureDepuisBL(expeditionId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operations', 'beton', 'expeditions'] });
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] });
      qc.invalidateQueries({ queryKey: BALANCE_AGEE_KEY });
    },
  });
};

export const useCreerAvoir = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ factureId, payload }: { factureId: string; payload: CreerAvoirPayload }) =>
      operationsApi.beton.creerAvoir(factureId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] });
      qc.invalidateQueries({ queryKey: BALANCE_AGEE_KEY });
    },
  });
};

// ── Balance âgée ─────────────────────────────────────────────────────────

export const useBalanceAgee = () =>
  useQuery({
    queryKey:             BALANCE_AGEE_KEY,
    queryFn:              () => financeApi.getBalanceAgee(),
    // Ticket SIMGROUP-2026-002 M-06 : la balance âgée est un état financier
    // qui doit refléter la date du jour, pas un snapshot périmé.
    staleTime:            0,           // toujours considérée comme stale
    refetchOnMount:       'always',    // recalcul à chaque ouverture du module
    refetchOnWindowFocus: true,        // recalcul quand l'user revient sur l'onglet
    refetchInterval:      300_000,     // rafraîchissement passif toutes les 5min
  });

// ── LOT A : indicateurs financiers (source unique) ───────────────────────

export const INDICATEURS_FINANCIERS_KEY = ['finance', 'indicateurs'] as const;

/**
 * Tout écran qui affiche un montant financier lit CE hook. Il n'additionne
 * pas de factures lui-même : c'est ce qui a produit trois encours
 * différents au même instant le 22/09/2026.
 */
export const useIndicateursFinanciers = () =>
  useQuery({
    queryKey:             INDICATEURS_FINANCIERS_KEY,
    queryFn:              () => financeApi.getIndicateurs(),
    staleTime:            0,
    refetchOnMount:       'always',
    refetchOnWindowFocus: true,
    refetchInterval:      300_000,
  });

// ── Réclamations chantier ────────────────────────────────────────────────

export const useReclamations = (params: ReclamationListParams = {}) =>
  useQuery({
    queryKey:  [...RECLAMATIONS_KEY, params],
    queryFn:   () => operationsApi.beton.listReclamations(params),
    staleTime: 30_000,
  });

export const useReclamation = (id: string | undefined) =>
  useQuery({
    queryKey:  [...RECLAMATIONS_KEY, id],
    queryFn:   () => operationsApi.beton.getReclamation(id!),
    enabled:   !!id,
    staleTime: 15_000,
  });

export const useCreateReclamation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateReclamationPayload) => operationsApi.beton.createReclamation(payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: RECLAMATIONS_KEY }),
  });
};

export const useUpdateReclamation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateReclamationPayload }) =>
      operationsApi.beton.updateReclamation(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: RECLAMATIONS_KEY }),
  });
};

export const useDeleteReclamation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteReclamation(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: RECLAMATIONS_KEY }),
  });
};
